import { booleanAttribute } from "#ce/utils/props";

const variantOptions = ["text", "flat", "elevated", "tonal", "outlined", "plain", "new-default"];

export const props = Object.freeze({
  /**
   * Convenience prop that renders an sp-card-title row. Ignored when the title slot is provided.
   *
   * @type {String}
   * @default undefined
   */
  title: {
    type: String,
    default: undefined,
  },
  /**
   * Convenience prop that renders an sp-card-subtitle row. Ignored when the subtitle slot is provided.
   *
   * @type {String}
   * @default undefined
   */
  subtitle: {
    type: String,
    default: undefined,
  },
  /**
   * Visual emphasis level. "plain" removes padding, shadow, and border; "flat" removes shadow and border but keeps padding; "new-default" removes shadow and border with comfortable padding (16px sides/bottom, 12px top) for a card inside a surface that supplies its own border/shadow, e.g. an sp-popup menu; "outlined" adds a 1px outline; "elevated" adds elevation-1 shadow; "tonal" applies a tinted background; "text" removes the surface entirely.
   *
   * @type {String}
   * @default "plain"
   * @values text, flat, elevated, tonal, outlined, plain, new-default
   * @storybook-control select
   */
  variant: {
    type: String,
    default: "plain",
    validator: (value) => variantOptions.includes(value),
  },
  /**
   * Variant override applied below the small breakpoint.
   *
   * @type {String}
   * @default undefined
   * @values text, flat, elevated, tonal, outlined, plain, new-default
   * @storybook-control select
   */
  smVariant: {
    type: String,
    default: undefined,
    validator: (value) => variantOptions.includes(value),
  },
  /**
   * Box-shadow elevation level applied to the surface. Overrides the variant default when set.
   *
   * @type {Number}
   * @default undefined
   * @storybook-control select
   */
  elevation: {
    type: Number,
    default: undefined,
    validator: (value) => [1, 2, 3].includes(value),
  },
  /**
   * Lay out the title, subtitle, content, and actions rows on a CSS grid with a fixed content row.
   *
   * @type {Boolean|String}
   * @default false
   */
  grid: booleanAttribute(),
  /**
   * Allow the content row to scroll vertically when it exceeds the card's height. Implies grid.
   *
   * @type {Boolean|String}
   * @default false
   */
  scrollableContent: booleanAttribute(),
});
